// LOGOMAN PRO — parse a Photoshop .psd file into useful logo seeds.
// Uses ag-psd to read layers; pulls the first text layer as the title and
// runs the rasterised canvas through our existing palette extractor.

import { readPsd } from "ag-psd";
import { extractPaletteFromImage, type ExtractedColor } from "./colorExtractor";

export interface ImportedPsd {
  width: number;
  height: number;
  /** First text layer found (most likely the brand name). */
  title?: string;
  /** Second text layer if present (often a tagline). */
  slogan?: string;
  /** Palette extracted from the rendered canvas. */
  palette: ExtractedColor[];
  /** Object URL for a thumbnail of the rendered file. Caller should revoke. */
  thumbnailURL: string;
}

export async function importPsd(file: File): Promise<ImportedPsd> {
  const buffer = await file.arrayBuffer();
  const psd = readPsd(buffer);

  // Walk all layers (recursively) and collect text strings in document order.
  const texts: string[] = [];
  walk(psd.children || [], (layer) => {
    if (layer.text && layer.text.text) {
      const t = String(layer.text.text).trim();
      if (t) texts.push(t);
    }
  });

  // Use the canvas the lib renders for us
  const canvas = psd.canvas;
  let palette: ExtractedColor[] = [];
  let thumbnailURL = "";

  if (canvas) {
    // Rasterise to a Blob for thumbnail and palette extraction
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b: Blob | null) => resolve(b), "image/png")
    );
    if (blob) {
      thumbnailURL = URL.createObjectURL(blob);
      const img = await loadImage(thumbnailURL);
      palette = extractPaletteFromImage(img, { count: 8 });
    }
  }

  return {
    width: psd.width,
    height: psd.height,
    title: texts[0],
    slogan: texts[1],
    palette,
    thumbnailURL,
  };
}

interface PsdNode {
  text?: { text?: string };
  children?: PsdNode[];
}

function walk(nodes: PsdNode[], visit: (n: PsdNode) => void) {
  for (const n of nodes) {
    visit(n);
    if (n.children && n.children.length) walk(n.children, visit);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
